import { cn } from '@mgrvtechgroup/admin-ds';
import { format, isToday } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  formatTime,
  groupByDate,
  HOUR_END,
  HOUR_START,
  layoutDay,
  toDateKey,
  weekDays,
  type CalEvent,
} from './event-calendar-model';
import { describeEvent, EVENT_CHIP } from './event-style';

/** 한 시간의 높이(px) — 30분 일정도 두 줄(제목+시각)이 들어갈 만큼 */
const HOUR_PX = 48;
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
const TIME_COLUMN = 56;

/**
 * 주 보기 — 7일 × 시간대 그리드. 시간 칸은 모두 "그 시각에 일정 추가" 버튼이고,
 * 일정 블록은 그 위 층에 절대 위치로 올라간다. 겹치는 일정은 layoutDay가 나란히 세운다.
 * 날짜 머리와 시간 열은 sticky라 가로·세로로 스크롤해도 어느 날 몇 시인지 보인다.
 */
export function WeekView({
  anchor,
  events,
  onAdd,
  onOpenEvent,
}: {
  anchor: Date;
  events: CalEvent[];
  onAdd: (date: string, hour: number) => void;
  onOpenEvent: (id: string) => void;
}) {
  const days = weekDays(anchor);
  const byDate = groupByDate(events);

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div
        className="grid min-w-[760px]"
        style={{ gridTemplateColumns: `${String(TIME_COLUMN)}px repeat(7, minmax(0, 1fr))` }}
      >
        <div className="bg-background sticky top-0 left-0 z-30 border-b" />
        {days.map((day) => (
          <div
            key={toDateKey(day)}
            className="bg-background sticky top-0 z-20 flex items-center justify-center gap-1.5 border-b border-l py-2"
          >
            <span className="caption text-muted-foreground">
              {format(day, 'EEE', { locale: ko })}
            </span>
            <span
              className={cn(
                'caption inline-flex size-6 items-center justify-center rounded-full tabular-nums',
                isToday(day) && 'bg-foreground text-background font-medium',
              )}
            >
              {format(day, 'd')}
            </span>
          </div>
        ))}

        {/* 시간 눈금 열 */}
        <div className="bg-background sticky left-0 z-10">
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="caption text-muted-foreground border-b pr-2 text-right tabular-nums"
              style={{ height: HOUR_PX }}
            >
              {formatTime(hour * 60)}
            </div>
          ))}
        </div>

        {days.map((day) => {
          const key = toDateKey(day);
          return (
            <div key={key} className={cn('relative border-l', isToday(day) && 'bg-muted/30')}>
              {HOURS.map((hour) => (
                <button
                  key={hour}
                  type="button"
                  aria-label={`${format(day, 'M월 d일 (EEE)', { locale: ko })} ${formatTime(hour * 60)} 일정 추가`}
                  onClick={() => {
                    onAdd(key, hour);
                  }}
                  className="hover:bg-muted/60 focus-visible:ring-ring block w-full border-b focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                  style={{ height: HOUR_PX }}
                />
              ))}
              {/* 일정 층 — 빈 곳은 아래 시간 버튼으로 클릭이 통과한다. */}
              <div className="pointer-events-none absolute inset-0">
                {layoutDay(byDate.get(key) ?? []).map((placed) => (
                  <button
                    key={placed.event.id}
                    type="button"
                    aria-label={describeEvent(placed.event)}
                    title={describeEvent(placed.event)}
                    onClick={() => {
                      onOpenEvent(placed.event.id);
                    }}
                    className={cn(
                      'caption focus-visible:ring-ring pointer-events-auto absolute flex flex-col items-start overflow-hidden rounded-sm border px-1.5 py-0.5 text-left hover:brightness-95 focus-visible:z-10 focus-visible:ring-2 focus-visible:outline-none',
                      EVENT_CHIP[placed.event.status],
                    )}
                    style={{
                      top: (placed.topMin / 60) * HOUR_PX + 1,
                      height: Math.max((placed.durationMin / 60) * HOUR_PX - 2, 20),
                      left: `calc(${String((placed.column / placed.columns) * 100)}% + 1px)`,
                      width: `calc(${String(100 / placed.columns)}% - 2px)`,
                    }}
                  >
                    <span className="w-full truncate font-medium">{placed.event.title}</span>
                    <span className="w-full truncate tabular-nums">
                      {formatTime(placed.event.startMin)} ~ {formatTime(placed.event.endMin)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
